import { useEffect, useRef, useState } from "react";

import { type ParseMealView, api } from "../lib/api";
import { blobToDataUrl, pickAudioMimeType } from "../lib/audio";
import { useLang } from "../lib/i18n";
import { mealsCopy } from "../lib/i18n/meals";
import { ScreenShell } from "./screen-shell";
import { IconCheck, IconMic } from "./ui/icon";
import { H1, TopBar } from "./ui/primitives";

type Phase = "idle" | "recording" | "uploading";

type Props = {
  onParsed: (parsed: ParseMealView) => void;
  onCancel: () => void;
};

// Number of bars in the decorative waveform. The first `filled` bars are
// accent-colored, the rest are dim. While recording, `filled` grows with
// seconds — gives the user a live "I'm listening" cue without WebAudio.
const WAVE_BARS = 40;
// Precomputed bar descriptors so we can use stable string keys (the bar set
// never reorders and is purely decorative, but biome's noArrayIndexKey rule
// still flags raw index keys).
const WAVE_BAR_KEYS = Array.from({ length: WAVE_BARS }, (_, i) => ({
  id: `wave-${i}`,
  index: i,
  baseH: 8 + Math.abs(Math.sin(i * 0.7)) * 60,
}));

function formatTimer(seconds: number): string {
  const mm = String(Math.floor(seconds / 60)).padStart(2, "0");
  const ss = String(seconds % 60).padStart(2, "0");
  return `${mm}:${ss}`;
}

export function MealVoiceScreen({ onParsed, onCancel }: Props) {
  const { lang } = useLang();
  const t = mealsCopy[lang];
  const [phase, setPhase] = useState<Phase>("idle");
  const [seconds, setSeconds] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const recorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const streamRef = useRef<MediaStream | null>(null);
  const tickRef = useRef<number | null>(null);

  useEffect(() => {
    return () => {
      if (tickRef.current) window.clearInterval(tickRef.current);
      for (const t of streamRef.current?.getTracks() ?? []) t.stop();
    };
  }, []);

  async function startRecording() {
    setError(null);
    setSeconds(0);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const mimeType = pickAudioMimeType();
      const recorder = new MediaRecorder(stream, mimeType ? { mimeType } : undefined);
      chunksRef.current = [];
      recorder.ondataavailable = (e) => {
        if (e.data && e.data.size > 0) chunksRef.current.push(e.data);
      };
      recorder.onstop = async () => {
        const blob = new Blob(chunksRef.current, { type: recorder.mimeType });
        for (const t of streamRef.current?.getTracks() ?? []) t.stop();
        streamRef.current = null;
        if (tickRef.current) {
          window.clearInterval(tickRef.current);
          tickRef.current = null;
        }
        if (blob.size === 0) {
          setPhase("idle");
          setError(t.voiceEmptyRecording);
          return;
        }
        setPhase("uploading");
        try {
          const dataUrl = await blobToDataUrl(blob);
          const parsed = await api.parseMealVoice(dataUrl);
          if (parsed.items.length === 0) {
            setError(
              parsed.rawInputText
                ? t.voiceHeard(parsed.rawInputText)
                : t.voiceNothingHeard,
            );
            setPhase("idle");
            return;
          }
          onParsed(parsed);
        } catch (err) {
          setError(err instanceof Error ? err.message : t.voiceGenericError);
          setPhase("idle");
        }
      };
      recorder.start();
      recorderRef.current = recorder;
      setPhase("recording");
      tickRef.current = window.setInterval(() => setSeconds((s) => s + 1), 1000);
    } catch (err) {
      setError(
        err instanceof Error && err.name === "NotAllowedError"
          ? t.voiceMicDenied
          : err instanceof Error
            ? err.message
            : t.voiceMicError,
      );
      setPhase("idle");
    }
  }

  function stopRecording() {
    recorderRef.current?.stop();
    recorderRef.current = null;
  }

  const supported = typeof MediaRecorder !== "undefined";
  const isRecording = phase === "recording";
  const isUploading = phase === "uploading";
  const filledBars = isRecording
    ? Math.min(WAVE_BARS, Math.floor((seconds % 6) * (WAVE_BARS / 6)) + 6)
    : isUploading
      ? WAVE_BARS
      : 0;

  return (
    <ScreenShell hideHeader>
      <TopBar back={t.voiceBack} title={t.voiceTitle} onBack={onCancel} />

      <div style={{ marginTop: 18 }}>
        <H1 accent=".">{t.voiceH1}</H1>
        <p className="text-[14px] leading-snug" style={{ marginTop: 10, color: "var(--ink-2)" }}>
          {t.voiceLead}
        </p>
      </div>

      {/* Waveform card — dark surface, status header, bar array, transcript. */}
      <div
        className="relative"
        style={{
          marginTop: 28,
          padding: "32px 24px",
          borderRadius: 18,
          background: "var(--ink)",
          color: "var(--bg)",
          boxShadow: "0 10px 30px rgba(0,0,0,0.12)",
        }}
      >
        <div className="flex items-center justify-between">
          <span
            style={{
              fontFamily: "var(--font-mono)",
              fontSize: 10,
              color: isRecording || isUploading ? "var(--accent)" : "rgba(255,255,255,0.5)",
              letterSpacing: "0.1em",
            }}
          >
            {isUploading
              ? t.voiceStatusUploading
              : isRecording
                ? t.voiceStatusRecording
                : t.voiceStatusIdle}
          </span>
          <span
            className="tabular"
            style={{
              fontFamily: "var(--font-mono)",
              fontSize: 14,
              color: "var(--bg)",
            }}
          >
            {formatTimer(seconds)}
          </span>
        </div>

        {/* Decorative waveform. Bar height is deterministic so it doesn't
            jitter on re-render; we just shift the accent threshold. */}
        <div
          aria-hidden="true"
          className="flex items-center justify-between"
          style={{ gap: 3, marginTop: 24, marginBottom: 24, height: 80 }}
        >
          {WAVE_BAR_KEYS.map(({ id, index, baseH }) => {
            const accented = index < filledBars;
            return (
              <div
                key={id}
                style={{
                  flex: 1,
                  height: accented ? baseH + 6 : 14,
                  borderRadius: 2,
                  background: accented ? "var(--accent)" : "rgba(255,255,255,0.18)",
                  transition: "height 200ms ease, background 200ms ease",
                }}
              />
            );
          })}
        </div>

        {/* Live transcript area — shows hint until we have something useful. */}
        <div
          style={{
            fontFamily: "var(--font-mono)",
            fontSize: 13,
            color: "rgba(255,255,255,0.85)",
            lineHeight: 1.6,
            minHeight: 42,
          }}
        >
          {isUploading ? (
            <>
              <span style={{ color: "var(--bg)" }}>{t.voiceTranscriptUploading}</span>
              <span
                aria-hidden="true"
                className="animate-blink"
                style={{
                  display: "inline-block",
                  width: 8,
                  height: 14,
                  background: "var(--accent)",
                  marginLeft: 4,
                  verticalAlign: "-2px",
                }}
              />
            </>
          ) : isRecording ? (
            <>
              <span style={{ color: "rgba(255,255,255,0.5)" }}>{t.voiceTranscriptRecording}</span>
              <span
                aria-hidden="true"
                className="animate-blink"
                style={{
                  display: "inline-block",
                  width: 8,
                  height: 14,
                  background: "var(--accent)",
                  marginLeft: 4,
                  verticalAlign: "-2px",
                }}
              />
            </>
          ) : (
            <span style={{ color: "rgba(255,255,255,0.4)" }}>{t.voiceTranscriptIdle}</span>
          )}
        </div>
      </div>

      {/* Hint card. */}
      <div
        style={{
          marginTop: 18,
          padding: "14px 16px",
          borderRadius: 10,
          background: "var(--surface-2)",
        }}
      >
        <div
          style={{
            fontFamily: "var(--font-mono)",
            fontSize: 10,
            fontWeight: 700,
            letterSpacing: "0.08em",
            color: "var(--muted)",
          }}
        >
          {t.voiceHintLabel}
        </div>
        <div
          style={{
            marginTop: 6,
            fontSize: 12,
            color: "var(--ink-2)",
            lineHeight: 1.5,
          }}
        >
          {t.voiceHintBody}
        </div>
      </div>

      {!supported ? (
        <p
          className="text-[12px] px-3 py-2 rounded-md"
          style={{ background: "var(--bad-bg)", color: "var(--bad-fg)", marginTop: 16 }}
        >
          {t.voiceUnsupported}
        </p>
      ) : null}

      {error ? (
        <p
          className="text-[12px] px-3 py-2 rounded-md"
          style={{ background: "var(--bad-bg)", color: "var(--bad-fg)", marginTop: 16 }}
        >
          {error}
        </p>
      ) : null}

      {/* Action row — cancel · mic · stop. */}
      <div
        className="flex items-center justify-center gap-2.5"
        style={{ marginTop: 24, marginBottom: 8 }}
      >
        <button
          type="button"
          onClick={onCancel}
          disabled={isUploading}
          aria-label={t.voiceCancelAria}
          className="flex items-center justify-center disabled:opacity-50"
          style={{
            width: 54,
            height: 54,
            borderRadius: 99,
            background: "var(--surface)",
            color: "var(--ink)",
            border: "none",
            boxShadow: "0 0 0 1px var(--nav-border)",
            fontSize: 18,
            fontWeight: 700,
          }}
        >
          ×
        </button>
        <button
          type="button"
          onClick={isRecording ? stopRecording : startRecording}
          disabled={!supported || isUploading}
          aria-label={isRecording ? t.voiceStopAria : t.voiceRecordAria}
          className="flex items-center justify-center disabled:opacity-50"
          style={{
            width: 76,
            height: 76,
            borderRadius: 99,
            background: "var(--accent)",
            color: "var(--accent-ink)",
            border: "none",
            boxShadow: isRecording
              ? "0 0 0 6px rgba(255,90,31,0.25), var(--shadow-cta)"
              : "0 0 0 6px rgba(255,90,31,0.15), var(--shadow-cta)",
          }}
        >
          {isRecording ? (
            <span
              aria-hidden="true"
              style={{
                width: 22,
                height: 22,
                background: "var(--accent-ink)",
                borderRadius: 4,
              }}
            />
          ) : (
            <IconMic size={28} />
          )}
        </button>
        <button
          type="button"
          onClick={isRecording ? stopRecording : undefined}
          disabled={!isRecording}
          aria-label={t.voiceDoneAria}
          className="flex items-center justify-center disabled:opacity-30"
          style={{
            width: 54,
            height: 54,
            borderRadius: 99,
            background: "var(--surface)",
            color: "var(--ink)",
            border: "none",
            boxShadow: "0 0 0 1px var(--nav-border)",
          }}
        >
          <IconCheck size={22} />
        </button>
      </div>
    </ScreenShell>
  );
}
